<template>
  <div id="insurance-landing-page" v-if="!isLoading">
    <div class="insurance-landing pt-10">
      <HeroPictureForm v-bind="heroInfo" />

      <div class="container flex flex-wrap justify-center">
        <div v-for="tile in features" :key="tile.title" class="flex">
          <div
            class="mb-8 mx-2 flex justify-center md:max-w-[18rem] flex-col flex-wrap text-center"
          >
            <img :src="tile.image.fields.file.url" />
            <TypeBody variant="large" weight="strong" class="mb-2">
              {{ tile.title }}
            </TypeBody>
            <TypeBody variant="medium" tag="div">
              {{ tile.description }}
            </TypeBody>
          </div>
        </div>
      </div>
    </div>

    <Ribbon variant="primary" />

    <div class="max-w-7xl mx-auto py-10 px-4">
      <InfoBlock
        v-for="(row, index) in insuranceFeatures"
        :key="`insurance-${index}`"
        v-bind="row"
        :reverse="index === 1"
        class="my-12"
      />
    </div>

    <Ribbon variant="gray" />

    <div class="container md:grid md:grid-cols-2">
      <div class="col-start-2">
        <img class="md:-ml-24" src="@/assets/images/agent-seal.png" />
        <div class="-mt-32">
          <TypeDisplay tag="div" variant="peta" class="mb-4">
            Bambee Honest Agent &trade;
          </TypeDisplay>
          <TypeHeading variant="headline" weight="regular" tag="p">
            How do you know which coverage and policy is right for your company?
            With so much jargon and legalese, it&#39;s hard to know if
            you&#39;re making the right decision. You aren&#39;t alone. We think
            it&#39;s harder than it should be. That&#39;s where our Bambee
            Honest Agents come in. Here&#39;s what they do&#58;
          </TypeHeading>
          <div class="mt-4">
            <div v-for="bullet in bullets" :key="bullet">
              <div class="flex my-2">
                <div>
                  <BaseIcon class="text-success mr-4" icon="check" />
                </div>
                <TypeHeading tag="div" variant="headline" weight="strong">
                  {{ bullet }}
                </TypeHeading>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Ribbon variant="gray" />

    <div class="container mt-8">
      <div class="flex justify-center flex-row">
        <div class="flex">
          <RegistrationPrompt
            button-id="company-size-button"
            class="registration-prompt pb-4 mt-16 lg:mt-0 drop-shadow-xl"
          />
        </div>
      </div>
    </div>

    <div class="bg-primary flex flex-col items-center mb-20 p-14">
      <div class="max-w-2xl text-center">
        <TypeDisplay variant="mega" class="text-white">
          You can get 250 years of Bambee for the price of the average
          harassment lawsuit settlement
        </TypeDisplay>
        <TypeBody variant="medium" tag="div" class="text-gray-3">
          *The average harassment lawsuit settlement in 2018 was $600k
        </TypeBody>
      </div>
    </div>
  </div>
</template>

<script>
import * as Cookies from 'js-cookie';
import { TypeBody, TypeDisplay, TypeHeading, BaseIcon } from '@bambeehr/pollen';
import { ref, onMounted } from '@nuxtjs/composition-api';
import HeroPictureForm from '@/components/HeroPictureForm';
import Ribbon from '@/components/Ribbon.vue';
import RegistrationPrompt from '@/components/RegistrationPrompt';
import Contentful from '@/services/Contentful';
import ContentfulEntryIDs from '@/lib/global/ContentfulEntryIDs';
import { insuranceFeatures } from '@/modules/InformationPages/Insurance/mocks/index';
import InfoBlock from '@/components/InfoBlock';
import { trainingInfoBlocks } from '@/modules/InformationPages/Training/mocks/index';

const fetchContent = async () =>
  await Contentful.getEntry(ContentfulEntryIDs.PAGE_INSURANCE);

const bullets = [
  'Get to know you and your business',
  'Figure out precisely what coverage is essential',
  'Make it easy to understand and compare your choices',
];

export default {
  name: 'InsuranceLandingPage',
  components: {
    HeroPictureForm,
    TypeHeading,
    BaseIcon,
    Ribbon,
    RegistrationPrompt,
    TypeBody,
    TypeDisplay,
    InfoBlock,
  },
  setup() {
    const heroInfo = ref({});
    const showInsuranceDisclaimer = ref(false);
    const isLoading = ref(true);

    const asyncData = async () => {
      const content = await fetchContent();
      isLoading.value = false;
      return {
        content,
      };
    };

    asyncData().then((data) => {
      return (heroInfo.value = {
        title: data.content.fields.heroFormTitle,
        heroImage: data.content.fields.heroFormImage.fields.file.url,
      });
    });

    const promptInsuranceDisclaimer = () => {
      showInsuranceDisclaimer.value = true;
    };

    const acceptInsuranceDisclaimer = () => {
      Cookies.set('acceptedInsuranceDisclaimer', true);
      showInsuranceDisclaimer.value = false;
    };

    onMounted(() => {
      const acceptedCookie = Cookies.get('acceptedInsuranceDisclaimer');
      if (!acceptedCookie) {
        promptInsuranceDisclaimer();
      }
    });

    return {
      insuranceFeatures,
      trainingInfoBlocks,
      heroInfo,
      bullets,
      showInsuranceDisclaimer,
      acceptInsuranceDisclaimer,
      isLoading,
    };
  },
};
</script>
<style scoped>
.container {
  flex: 1 1 100%;
  margin: auto;
  padding: 48px;
  width: 100%;
}
.layout {
  display: flex;
  flex: 1 1 auto;
  flex-wrap: nowrap;
  min-width: 0;
}
</style>
